import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

import { selectAuthenticationError } from '../store/authentication.selector';
import { clearAuthenticationError, signIn } from '../store/authentication.action';
import { JWTService } from '../service/jwt.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { PrimaryButtonComponent } from '../../shared/buttons/primary-button.component';
import { TertiaryButtonComponent } from '../../shared/buttons/tertiary-button.component';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'sibat-signin',
  template: `
    <form *transloco="let t; read: 'authentication'">
      @if (errorMessage$ | async; as errMsg) {
      <p class="custom-msg error-msg">
        {{ t(errMsg) }}
      </p>
      } @if (successMessage$ | async; as succMsg) {
      <p class="custom-msg success-msg">
        {{ t(succMsg) }}
      </p>
      }

      <mat-form-field [formGroup]="signInForm" [class.margin-bottom]="email.hasError('email')">
        <mat-label>{{ t('email') }}</mat-label>
        <input matInput type="email" formControlName="email" required="required" />
        @if (email.hasError('email')) {
        <mat-error>
          {{ t('error.requiredValidEmail') }}
        </mat-error>
        }
      </mat-form-field>

      <mat-form-field [formGroup]="signInForm">
        <mat-label>{{ t('password') }}</mat-label>
        <input #pwdInput matInput type="password" formControlName="password" required="required" />
      </mat-form-field>

      <sibat-primary-button
        submit="true"
        (clicked)="sendLogin()"
        [disabled]="signInForm.touched && signInForm.invalid"
        [label]="t('login')"
      ></sibat-primary-button>

      <br /><br />

      <sibat-tertiary-button (clicked)="forgotPassword()" [label]="t('passwordForgotten')"></sibat-tertiary-button>
    </form>
  `,
  styleUrls: ['sign-in.component.scss'],
  imports: [
    TranslocoDirective,
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    PrimaryButtonComponent,
    TertiaryButtonComponent,
    AsyncPipe,
  ],
})
export class SignInComponent implements OnInit {
  @ViewChild('pwdInput') pwdInput!: ElementRef;

  signInForm: UntypedFormGroup;
  errorMessage$: Observable<string | null>;
  successMessage$: Observable<string | null>;

  readonly email = this.formBuilder.control('', [Validators.required, Validators.email]);
  readonly password = this.formBuilder.control('', Validators.required);

  private readonly ecabOnlySubject = new BehaviorSubject<string | null>(null);
  private readonly logoutSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private jwtService: JWTService,
    private route: ActivatedRoute,
    private cookieService: CookieService
  ) {
    this.signInForm = this.formBuilder.group({
      email: this.email,
      password: this.password,
    });

    const urlErrorCode$ = this.route.queryParamMap.pipe(map(params => params.get('error')));

    const authError$ = this.store.select(selectAuthenticationError).pipe(map(err => (err ? 'error.loginInvalid' : null)));

    this.errorMessage$ = combineLatest([urlErrorCode$, this.ecabOnlySubject.asObservable(), authError$]).pipe(
      map(([urlCode, ecabMsg, authErr]) => {
        if (ecabMsg) {
          return ecabMsg;
        }

        if (authErr) {
          return authErr;
        }

        if (urlCode === 'user_not_found') {
          return 'error.user_not_found';
        }

        return null;
      })
    );

    this.successMessage$ = this.logoutSubject.asObservable();
  }

  sendLogin(): void {
    const email = (this.signInForm.value.email ?? '').trim();

    if (email.toLowerCase().endsWith('@ecab.ch')) {
      this.ecabOnlySubject.next('error.ecab_user_only_sso');
      return;
    }

    this.ecabOnlySubject.next(null);
    this.store.dispatch(
      signIn({
        email,
        password: this.signInForm.value.password,
      })
    );
  }

  forgotPassword(): void {
    this.store.dispatch(clearAuthenticationError());
    this.router.navigate(['/forgotPassword']);
  }

  ngOnInit(): void {
    this.jwtService.clearToken();

    if (this.cookieService.check('logout')) {
      this.cookieService.delete('logout', '/');
      this.logoutSubject.next('logout_success');
    }
  }
}
